import { IconButton, TextField } from '@mui/material'
import React, { useMemo, useState } from 'react'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { useStyles } from './PrivateOfferDimensions.styles'
import { KeyValue } from '../../../modules/privateOffer/privateOfferCreation/types'
import { Dimension, PriceTypes } from '../../../modules/privateOffer/types'
import { getRandom, preventEventForKeys } from '@labrav/common'
import { isEmpty } from 'lodash'
import clsx from 'clsx'
import { getCustomDimensionPriceMappings } from './utils'

export type CustomDimensionRowProps = {
  index: number
  dimensionsLength: number
  customDimension: Dimension
  deleteRow: () => void
  updateDimensions: (data: KeyValue) => void
  errors: Record<string, unknown>
  flexiblePaymentTerms: boolean
  pricesArray?: [keyof PriceTypes]
  isCustom?: boolean
}

export const CustomDimensionRow = React.memo(
  ({
    index,
    dimensionsLength,
    customDimension,
    updateDimensions,
    deleteRow,
    errors,
    flexiblePaymentTerms,
    pricesArray = ['price'],
    isCustom = false,
  }: CustomDimensionRowProps) => {
    const { classes } = useStyles()
    const randomKey = useMemo(getRandom, [index, dimensionsLength])
    const [quantityValue, setQuantityValue] = useState(
      customDimension?.quantity ?? ''
    )

    const handleInputChange =
      (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const dataValue =
          key === 'quantity'
            ? Math.round(Number(event.target.value))
            : (event.target.value as unknown as number)
        if (key === 'quantity') {
          setQuantityValue(dataValue)
        }
        updateDimensions({
          key,
          value: [...pricesArray, 'quantity'].includes(key)
            ? dataValue
            : (event.target.value as string),
        })
      }
    const mappings: Record<string, string> = useMemo(
      () => getCustomDimensionPriceMappings(isCustom),
      [isCustom]
    )
    return (
      <div
        className={classes.updateRow}
        data-testid="dimension-row"
        key={`${randomKey}-${index}`}
      >
        <TextField
          id="label"
          label="Label"
          variant="standard"
          required={true}
          key={'label'}
          defaultValue={customDimension.label}
          className={classes.textField}
          onChange={handleInputChange('label')}
          error={!isEmpty(errors?.label as string)}
          helperText={errors?.label as string}
          slotProps={{
            input: { inputProps: { 'data-testid': 'custom-label' } },
          }}
        />
        <TextField
          id="labelDescription"
          label="Description"
          variant="standard"
          required={true}
          defaultValue={customDimension.labelDescription}
          className={clsx(classes.textField, classes.description)}
          onChange={handleInputChange('labelDescription')}
          error={!isEmpty(errors?.labelDescription as string)}
          helperText={errors?.labelDescription as string}
          slotProps={{
            input: { inputProps: { 'data-testid': 'labelDescription' } },
          }}
        />
        <TextField
          id="name"
          label="API name"
          variant="standard"
          required={true}
          key={'name'}
          defaultValue={customDimension.name}
          className={classes.textField}
          onChange={handleInputChange('name')}
          error={!isEmpty(errors?.name as string)}
          helperText={errors?.name as string}
          slotProps={{
            input: {
              inputProps: { 'data-testid': 'custom-name', maxLength: 16 },
            },
          }}
        />
        {flexiblePaymentTerms ? (
          <TextField
            id="quantity"
            label="QUANTITY"
            key={`quantity-${randomKey}`}
            variant="standard"
            required={true}
            defaultValue={quantityValue}
            value={quantityValue}
            className={classes.textField}
            onChange={handleInputChange('quantity')}
            onKeyDown={preventEventForKeys(['+', '-', '.', 'e'])}
            type={'number'}
            error={!isEmpty(errors?.quantity as string)}
            helperText={errors?.quantity as string}
            slotProps={{
              input: {
                inputProps: { 'data-testid': 'custom-quantity', min: 0 },
              },
            }}
          />
        ) : (
          pricesArray.map(price => (
            <TextField
              id={price}
              label={mappings[price]}
              key={price}
              variant="standard"
              required={true}
              defaultValue={(customDimension?.price as PriceTypes)[price] ?? ''}
              className={classes.textField}
              onChange={handleInputChange(price)}
              onKeyDown={preventEventForKeys(['+', '-', 'e'])}
              type={'number'}
              error={!isEmpty(errors?.[price] as string)}
              helperText={errors?.[price] as string}
              slotProps={{
                input: {
                  inputProps: { 'data-testid': `custom-${price}`, min: 0 },
                },
              }}
            />
          ))
        )}
        <IconButton
          className={classes.closeIconButton}
          onClick={deleteRow}
          data-testid="cancel-button"
          size="large"
        >
          <HighlightOffIcon className={classes.closeIcon} />
        </IconButton>
      </div>
    )
  }
)
